// Catalog data + shared helpers consumed by both the customer app
// (apps/web) and the public marketing site (apps/marketing).
//
// Each subdirectory exports a typed list of product cards generated
// from scripts/data/*.csv. UI components stay in the consuming apps.

export * from './shared';

export {
  ORTHOPAEDIC_PRODUCTS,
  ORTHOPAEDIC_FAMILIES,
  ORTHOPAEDIC_IMAGE_BASE,
  type OrthopaedicProduct,
  type OrthopaedicFamily,
} from './orthopaedic/catalog';

export {
  FITO_PRODUCTS,
  FITO_FAMILIES,
  FITO_IMAGE_BASE,
  type FitoProduct,
  type FitoFamily,
} from './fito/catalog';

export {
  READY_MADE_PRODUCTS,
  READY_MADE_FAMILIES,
  READY_MADE_IMAGE_BASE,
  type ReadyMadeProduct,
  type ReadyMadeFamily,
} from './ready-made/catalog';

export {
  CUSTOM_MADE_PRODUCTS,
  CUSTOM_MADE_IMAGE_BASE,
  type CustomMadeProduct,
  type CustomMadeFamily,
} from './custom-made/catalog';

/**
 * Convenience: every product card across every catalog, projected to a
 * common shape. The marketing /catalog page uses this to render a
 * uniform grid filtered by line_type. Sized catalogs (FITO, Ready-Made)
 * flatten their `sizes` array onto `formats`. Custom-Made cards carry
 * extra fields (deliveryDays, materials) used by the dedicated
 * "Find a clinic" product page treatment.
 */
import {
  ORTHOPAEDIC_PRODUCTS,
  ORTHOPAEDIC_IMAGE_BASE,
} from './orthopaedic/catalog';
import { FITO_PRODUCTS, FITO_IMAGE_BASE } from './fito/catalog';
import { READY_MADE_PRODUCTS, READY_MADE_IMAGE_BASE } from './ready-made/catalog';
import { CUSTOM_MADE_PRODUCTS, CUSTOM_MADE_IMAGE_BASE } from './custom-made/catalog';

export interface UnifiedCatalogCard {
  code: string;
  productName: string;
  description: string;
  /** Catalog admin line_type the card belongs to. */
  lineType: string;
  /** Path under /public/ to the product photo used on the grid card. */
  imagePath: string;
  /** Optional images shown in a crossfading carousel on the dedicated
   *  /catalog/[code] product page. When undefined or empty the page
   *  falls back to a static `imagePath`. Used today for custom-made
   *  products to mirror the multi-shot carousels on /products. */
  detailImagePaths?: readonly string[];
  /** Available formats (size labels for sized catalogs, format strings
   *  for the orthopaedic catalog, empty for custom-made). */
  formats: readonly string[];
  /** True when the product description mentions SPECIAL ORDER —
   *  surfaces a softer "Available on request" tag on the marketing site. */
  specialOrder: boolean;
  /** Custom-made flag: changes the card + product page UX (no
   *  "Add to enquiry", shows delivery time, "Find a clinic" CTA). */
  customMade?: boolean;
  /** Marketing-only — populated only when customMade is true. */
  deliveryDays?: string;
  materials?: string;
}

const SPECIAL_ORDER_MARKER = 'SPECIAL ORDER';

export const ALL_CATALOG_CARDS: readonly UnifiedCatalogCard[] = [
  ...CUSTOM_MADE_PRODUCTS.map((p) => ({
    code: p.code,
    productName: p.productName,
    description: p.description,
    lineType: p.family,
    imagePath: `${CUSTOM_MADE_IMAGE_BASE}/${p.image}`,
    detailImagePaths: p.detailImages.map((s) => `${CUSTOM_MADE_IMAGE_BASE}/${s}`),
    formats: [] as readonly string[],
    specialOrder: false,
    customMade: true,
    deliveryDays: p.deliveryDays,
    materials: p.materials,
  })),
  ...READY_MADE_PRODUCTS.map((p) => ({
    code: p.code,
    productName: p.productName,
    description: p.description,
    lineType: p.family,
    imagePath: `${READY_MADE_IMAGE_BASE}/${p.image}`,
    formats: p.sizes,
    specialOrder: p.description.includes(SPECIAL_ORDER_MARKER),
  })),
  ...ORTHOPAEDIC_PRODUCTS.map((p) => ({
    code: p.code,
    productName: p.productName,
    description: p.description,
    lineType: p.family,
    imagePath: `${ORTHOPAEDIC_IMAGE_BASE}/${p.image}`,
    formats: p.formats,
    specialOrder: p.specialOrder,
  })),
  ...FITO_PRODUCTS.map((p) => ({
    code: p.code,
    productName: p.productName,
    description: p.description,
    lineType: p.family,
    imagePath: `${FITO_IMAGE_BASE}/${p.image}`,
    formats: p.sizes,
    specialOrder: false,
  })),
];

/** Quick lookup by card code. Used by the dedicated product page route. */
export function findCardByCode(code: string): UnifiedCatalogCard | null {
  const upper = code.toUpperCase();
  return (
    ALL_CATALOG_CARDS.find((c) => c.code.toUpperCase() === upper) ?? null
  );
}
